import { AfterViewInit, Component, ElementRef, ViewChild, computed, inject, input, output, signal } from '@angular/core';
import { ScorecardDisplayType, ScorecardV2, ScorecardV2Action, ScorecardV2Query } from '@fgb/core';
import { gsap } from 'gsap';

/** Returns the chain the scorecard belongs to, root first, or an empty list when it is not linked. */
function getChain(scorecards: ScorecardV2[], scorecardId: number): ScorecardV2[] {
  const byId = new Map(scorecards.map((s) => [s.ScorecardId, s]));
  let root = byId.get(scorecardId);
  if (!root || root.ParentId === null) return [];
  for (let i = 0; root.ParentId && byId.has(root.ParentId) && i < scorecards.length; i++) {
    root = byId.get(root.ParentId)!;
  }
  const chain = [root];
  let child = scorecards.find((s) => s.ParentId === chain[0].ScorecardId);
  while (child && chain.length < scorecards.length) {
    chain.push(child);
    const parentId = child.ScorecardId;
    child = scorecards.find((s) => s.ParentId === parentId);
  }
  return chain;
}

@Component({
  selector: 'fgbcl-scorecards-v2-description',
  templateUrl: './scorecards-v2-description.component.html',
  styleUrl: './scorecards-v2-description.component.scss',
})
export class ScorecardsV2DescriptionComponent implements AfterViewInit {
  private scorecards = inject(ScorecardV2Query).getScorecards();

  scorecardId = input.required<number>();
  closed = output<void>();

  @ViewChild('badgeTrack', { read: ElementRef }) badgeTrack?: ElementRef;

  /** Set when another scorecard is picked in the linked carousel */
  private selectedId = signal<number | null>(null);

  readonly linkedScorecards = computed(() => getChain(this.scorecards(), this.scorecardId()));
  readonly activeScorecard = computed(() => {
    const id = this.selectedId() ?? this.scorecardId();
    return this.scorecards().find((s) => s.ScorecardId === id);
  });
  readonly activeIndex = computed(() =>
    Math.max(0, this.linkedScorecards().findIndex((s) => s.ScorecardId === this.activeScorecard()?.ScorecardId))
  );
  readonly activeScorecardParent = computed(() => {
    const parentId = this.activeScorecard()?.ParentId;
    return parentId ? (this.scorecards().find((s) => s.ScorecardId === parentId) ?? null) : null;
  });
  readonly allActions = computed<ScorecardV2Action[]>(() => this.activeScorecard()?.Actions ?? []);
  readonly todoActions = computed(() => this.allActions().filter((a) => !a.ActionCompleted));
  readonly completedActions = computed(() => this.allActions().filter((a) => a.ActionCompleted));
  readonly milestoneActions = computed(() =>
    [...(this.activeScorecard()?.Milestone?.Milestones ?? [])].sort((a, b) => a.Ordinal - b.Ordinal)
  );

  descExpanded = false;
  tcsExpanded = false;
  detailsExpanded = false;
  isClosing = false;
  isSwitching = false;

  private readonly carouselItemWidth = 57;
  private readonly carouselGap = 40;

  ngAfterViewInit(): void {
    setTimeout(() => this.initCarousel());
  }

  selectAtIndex(index: number): void {
    const target = this.linkedScorecards()[index];
    if (!target || index === this.activeIndex()) return;
    this.isSwitching = true;
    this.selectedId.set(target.ScorecardId);
    this.descExpanded = false;
    this.tcsExpanded = false;
    this.detailsExpanded = false;
    setTimeout(() => (this.isSwitching = false));

    if (!this.badgeTrack?.nativeElement) return;
    const allItems = this.badgeTrack.nativeElement.querySelectorAll('.sc-badge-carousel-item');
    gsap.to(allItems, { scale: 1, opacity: 0.5, duration: 0.3, ease: 'power2.inOut' });
    gsap.to(this.badgeTrack.nativeElement, { x: this.getCarouselOffset(index), duration: 0.5, ease: 'power2.out' });

    setTimeout(() => {
      const selected = this.badgeTrack?.nativeElement.querySelector('.selected');
      if (selected) {
        gsap.to(selected, { scale: 1.4, opacity: 1, duration: 0.3, ease: 'power2.out' });
      }
    });
  }

  getProgressPercentage(): number {
    const scorecard = this.activeScorecard();
    if (!scorecard?.TotalToComplete) return 0;
    return Math.floor((scorecard.ActionsCompleted / scorecard.TotalToComplete) * 100);
  }

  getPrizeTitle(): string | undefined {
    return this.activeScorecard()?.PrizeDescription;
  }

  isStreakType(): boolean {
    return this.isDisplayType(ScorecardDisplayType.streakView);
  }

  isMilestoneType(): boolean {
    return this.isDisplayType(ScorecardDisplayType.milestoneProgressBarView);
  }

  isSoloAction(): boolean {
    return this.isDisplayType(ScorecardDisplayType.soloAction);
  }

  isSingleActionType(): boolean {
    return this.isDisplayType(ScorecardDisplayType.singleAction);
  }

  isCounterType(): boolean {
    return this.isDisplayType(ScorecardDisplayType.numberView);
  }

  isMilestoneReached(value: number): boolean {
    return (this.activeScorecard()?.Milestone?.CustomerValue ?? 0) >= value;
  }

  getMaxMilestone(): number {
    const milestones = this.activeScorecard()?.Milestone?.Milestones;
    if (!milestones?.length) return 0;
    return Math.max(...milestones.map((m) => m.Value));
  }

  getMsDotClass(index: number): string {
    const m = this.milestoneActions()[index];
    return m && this.isMilestoneReached(m.Value) ? 'ms-dot-completed' : 'ms-dot-future';
  }

  getMsLineClass(index: number): string {
    const m = this.milestoneActions()[index];
    return m && this.isMilestoneReached(m.Value) ? 'ms-line-completed' : 'ms-line-future';
  }

  close(): void {
    this.isClosing = true;
    setTimeout(() => this.closed.emit(), 300);
  }

  private isDisplayType(type: ScorecardDisplayType): boolean {
    return this.activeScorecard()?.ScoreCardDisplayType === type;
  }

  private getCarouselOffset(index: number): number {
    // Panel is min(430px, 100vw); sc-header has 20px padding on each side
    const containerWidth = Math.min(window.innerWidth, 430) - 40;
    return containerWidth / 2 - this.carouselItemWidth / 2 - index * (this.carouselItemWidth + this.carouselGap);
  }

  private initCarousel(): void {
    if (!this.badgeTrack?.nativeElement) return;
    gsap.set(this.badgeTrack.nativeElement, { x: this.getCarouselOffset(this.activeIndex()) });
    const allItems = this.badgeTrack.nativeElement.querySelectorAll('.sc-badge-carousel-item');
    gsap.set(allItems, { scale: 1, opacity: 0.5 });
    const selected = this.badgeTrack.nativeElement.querySelector('.selected');
    if (selected) gsap.set(selected, { scale: 1.4, opacity: 1 });
  }
}
